<template>
  <div>
    <!-- DIALOGS -->
    <SelectionRequiredDialog />
    <UsageImportDialog />
    <CompareUsageDialog />
    <UsageDeleteDialog />

    <BaseListPage
      headline="lang_PUBLISHING_USAGE"
      :context="context"
      :alvqa="alvqa"
      :showFiltersRow="false"
      :stickyHeader="true"
    >
      <template v-slot:filter>
        <v-col cols="2">
          <DropdownFilter
            context="UsageAreaFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_USAGE_AREA"
            :items="usageAreas"
            :defaultValueNull="true"
            :alvqa="alvqa + '-list-dropdown-usage-area-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsageDataTypeFilter"
            label="lang_PUBLISHING_USAGE_CAPTION_DATA_TYPE"
            :items="dataTypes"
            :defaultValueNull="true"
            :alvqa="alvqa + '-list-dropdown-data-type-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsageSourceFilter"
            label="lang_GENERIC_CAPTION_SOURCE"
            :items="sources"
            :defaultValueNull="true"
            :alvqa="alvqa + '-list-dropdown-source-filter'"
          />
        </v-col>
        <v-col cols="2">
          <DropdownFilter
            context="UsagePeriodFilter"
            label="lang_GENERIC_CAPTION_PERIOD"
            :items="periods"
            :defaultValueNull="true"
            :alvqa="alvqa + '-list-dropdown-period-filter'"
          />
        </v-col>
      </template>

      <template v-slot:dataTable>
        <BaseDataTableList
          :headers="headers"
          :context="context"
          :alvqa="alvqa"
          :hasServerSideSorting="true"
          :stickyTopSlot="true"
          customNoDataText="lang_GENERAL_MESSAGE_NO_RECORDS_FOUND"
        >
          <template v-slot:topSlot><UsageTopSlot /></template>

          <template v-slot:body="{ item }">
            <ExpandIcon
              :isExpanded="item.isExpanded"
              :alvqa="alvqa + '-list-row-' + item.item.id + '-icon-expand'"
            />
            <td class="ellipsis">
              <span class="d-inline-flex align-center" style="max-width: 100%">
                <v-progress-circular
                  v-if="item.item.importStatus === 'importing' || item.item.importStatus === 'deleting'"
                  indeterminate size="14" width="2" class="mr-2 flex-shrink-0"
                  color="navigationItemsIconsActive"
                />
                <BaseTooltip v-if="item.item.importStatus === 'failed'">
                  <template v-slot:content><v-icon small color="error" class="mr-2">error_outline</v-icon></template>
                  <template v-slot:text>{{ $t('lang_PUBLISHING_USAGE_CAPTION_STATUS_FAILED_HINT') }}</template>
                </BaseTooltip>
                <BaseTooltip
                  :simpleText="item.item.description || item.item.fileName"
                  :alvqa="alvqa + '-list-row-' + item.item.id + '-text-file'"
                />
              </span>
            </td>
            <td class="ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-data-type'">
              {{ $t(optionText(dataTypes, item.item.dataType)) }}
            </td>
            <td class="ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-source'">
              {{ item.item.source ? item.item.source.name : '–' }}
            </td>
            <td class="ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-usage-area'">
              {{ $t(optionText(usageAreas, item.item.usageArea)) }}
            </td>
            <td class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-period'">
              {{ item.item.period.label }}
            </td>
            <td class="no_ellipsis text-right" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-rows'">
              {{ $alvPlugin.getNumberLocalization(item.item.rowCount, 'numberInteger') }}
            </td>
            <td class="no_ellipsis" :alvqa="alvqa + '-list-row-' + item.item.id + '-text-imported'">
              {{ $d(toDate(item.item.importedAt), 'alvDateTime') }}
            </td>
            <td class="ellipsis">
              <BaseTooltip
                :simpleText="item.item.importedBy ? item.item.importedBy.name : '–'"
                :alvqa="alvqa + '-list-row-' + item.item.id + '-text-imported-by'"
              />
            </td>
            <td class="text-right" @click.stop="() => {}">
              <BaseIcon
                icon="cloud_download"
                tooltipText="lang_GENERAL_CAPTION_ICON_DOWNLOAD"
                :alvqa="alvqa + '-list-row-' + item.item.id + '-icon-download'"
                @click.stop="$store.dispatch('downloadUsageFile', item.item)"
              />
            </td>
          </template>

          <template v-slot:expandSlot="{ id }"><UsageReadOnly :datasetId="id" /></template>
        </BaseDataTableList>
      </template>
    </BaseListPage>
  </div>
</template>

<script>
import './usage.css'
import BaseDataTableList from '@/components/_base/BaseDataTableList'
import BaseListPage from '@/components/_base/BaseListPage'
import BaseTooltip from '@/components/_base/BaseTooltip'
import BaseIcon from '@/components/_buttons/BaseIcon'
import ExpandIcon from '@/components/_buttons/ExpandIcon'
import DropdownFilter from '@/components/_filters/DropdownFilter'
import SelectionRequiredDialog from '@/components/_dialogs/SelectionRequiredDialog'

import UsageTopSlot from './UsageTopSlot'
import UsageReadOnly from './_readonly/UsageReadOnly'
import UsageImportDialog from './_dialogs/UsageImportDialog'
import CompareUsageDialog from './_dialogs/CompareUsageDialog'
import UsageDeleteDialog from './_dialogs/UsageDeleteDialog'
import { USAGE_AREAS, DATA_TYPES, SOURCES, periodOptions, optionText } from './usageOptions'

export default {
  name: 'UsageList',
  components: {
    BaseDataTableList,
    BaseListPage,
    BaseTooltip,
    BaseIcon,
    ExpandIcon,
    DropdownFilter,
    SelectionRequiredDialog,
    UsageTopSlot,
    UsageReadOnly,
    UsageImportDialog,
    CompareUsageDialog,
    UsageDeleteDialog
  },
  data() {
    return {
      alvqa: 'publishing-usage',
      context: 'Usage',
      usageAreas: USAGE_AREAS,
      dataTypes: DATA_TYPES,
      sources: SOURCES,
      periods: periodOptions()
    }
  },
  computed: {
    headers() {
      const column = (text, value, width, sortable = true, align = 'left') => ({ text: text ? this.$t(text) : '', value, width, sortable, align, class: 'text-no-wrap' })
      return [
        column('', 'data-table-select', '60px', false, 'center'),
        column('', 'data-table-expand', '48px', false, 'center'),
        column('lang_PUBLISHING_USAGE_CAPTION_FILE_DESCRIPTION', 'fileName'),
        column('lang_PUBLISHING_USAGE_CAPTION_DATA_TYPE', 'dataType', '180px'),
        column('lang_GENERIC_CAPTION_SOURCE', 'source', '90px'),
        column('lang_PUBLISHING_USAGE_CAPTION_USAGE_AREA', 'usageArea', '110px'),
        column('lang_GENERIC_CAPTION_PERIOD', 'period', '110px'),
        column('lang_PUBLISHING_INCOME_CAPTION_RECORDS', 'rowCount', '70px', false, 'right'),
        column('lang_PUBLISHING_INCOME_CAPTION_IMPORT_DATE', 'importedAt', '180px'),
        column('lang_PUBLISHING_USAGE_CAPTION_IMPORTED_BY', 'importedBy', '140px'),
        column('', 'download', '56px', false, 'right')
      ]
    }
  },
  methods: {
    optionText,
    toDate(value) { return new Date(String(value).replace(' ', 'T')) }
  },
  created() {
    this.$store.dispatch('fetchUsageStationList')
  }
}
</script>
